import { API } from "@/main";
import store from "@/store";

const state = {
  multiObjectSelection: {
    polygon: undefined,
    projection: 3857,
    layers: undefined
  },

  selectedObjectIdentifiers: {},
  selectedObjectData: {},

  objectsProperties: [],

  activeNetworkFilters: [],

  closureWithoutTechPoint: { techPointType: undefined }
};

const getters = {
  getMultiObjectSelection: state => state.multiObjectSelection,
  selectedObjectIdentifiers: state => state.selectedObjectIdentifiers,
  selectedObjectData: state => state.selectedObjectData,
  getAllObjectTypesProperties: state => state.objectsProperties,
  layerExistsInObjectForms: state => layer => {
    return getLayerIndex(state.objectsProperties, layer) !== -1;
  },
  getLayerPermissionName: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    if (index !== -1) {
      if(state.objectsProperties[index].permission==="workpoint"){
        return "annotation_zone";
      }
      return state.objectsProperties[index].permission;
    } else return undefined;
  },

  getLayerEndpoint: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    return state.objectsProperties[index].endpoint;
  },

  getLayerOrderPosition: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    return state.objectsProperties[index].order;
  },

  getLayerTranslationKey: state => layer => {
    let layerTranslation = layer;

    let index = getLayerIndex(state.objectsProperties, layer);
    if (index >= 0) {
      let translationKey = state.objectsProperties[index].name_translation;
      if (translationKey) {
        layerTranslation = translationKey;
      }
    }

    return layerTranslation;
  },

  getLayerTypeName: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    return state.objectsProperties[index].type_name;
  },
  getLayerInitials: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    return state.objectsProperties[index].initials;
  },
  layerHasForm: state => layer => {
    let index = getLayerIndex(state.objectsProperties, layer);
    return state.objectsProperties[index].has_form;
  },

  getActiveNetworkFilters: state => {
    return state.activeNetworkFilters;
  },

  getClosureWithoutTechPoint: state => {
    return state.closureWithoutTechPoint.techPointType;
  }
};

const mutations = {
  updateMultiObjectSelection(state, data) {
    state.multiObjectSelection.polygon = data.polygonTextGeom;
    state.multiObjectSelection.projection = data.projection;
    state.multiObjectSelection.layers = data.layers;
  },

  clearMultiObjectSelection(state) {
    state.multiObjectSelection.polygon = undefined;
    state.multiObjectSelection.projection = undefined;
    state.multiObjectSelection.layers = undefined;
  },

  updateSelectedObjectIdentifiers(state, objectIdentifiers) {
    state.selectedObjectIdentifiers = objectIdentifiers;
  },
  updateSelectedObjectData(state, objectData) {
    state.selectedObjectData = objectData;
  },
  setAllObjectsPropertiesWithoutFields(state, objectsProperties) {
    state.objectsProperties = objectsProperties;
  },

  SET_ACTIVE_NETWORK_FILTERS(state, activeNetworkFilters) {
    state.activeNetworkFilters = activeNetworkFilters;
  },

  SET_CLOSURE_WITHOUT_TECH_POINT(state, techPointType) {
    state.closureWithoutTechPoint.techPointType = techPointType;
  }
};

const actions = {
  getAllObject(
    { commit },
    { endpoint, fields = null, includes = null, filters = null }
  ) {
    return new Promise((resolve, reject) => {
      API.networkObject.getAll(
        endpoint,
        fields,
        includes,
        filters,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  GET_OBJECTS_LAYERS_INSIDE({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.getInsidePolygon(
        data,
        result => {
          let layers = getMultiObjectSelectionTabs(result.data.data);
          let projection = data.projection;
          let polygonTextGeom = data.text_geom;
          commit("updateMultiObjectSelection", {
            polygonTextGeom,
            layers,
            projection
          });
          resolve(layers);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  UPDATE_MULTI_OBJECT_SELECTION({ commit }, data) {
    return new Promise(resolve => {
      let layers = data.layers;
      let polygonTextGeom = data.text_geom;
      let projection = data.projection;
      commit("updateMultiObjectSelection", {
        polygonTextGeom,
        layers,
        projection
      });
      resolve(true);
    });
  },

  GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON(
    { commit, state },
    {
      endpoint,
      technicalType = null,
      fieldFilters = null,
      fieldsToInclude = null,
      fieldsToAppend = null,
      usedByNetwork
    }
  ) {
    return new Promise((resolve, reject) => {
      API.networkObject.getAllWithinPolygon(
        endpoint,
        technicalType,
        fieldFilters,
        fieldsToInclude,
        fieldsToAppend,
        usedByNetwork,
        state.multiObjectSelection.polygon,
        state.multiObjectSelection.projection,
        false,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  // GET_ALL_BUILDINGS_NPV(
  //   { commit, state },
  //   {
  //     polygon = null,
  //     projection = null,
  //   }
  // ) {
  //   return new Promise((resolve, reject) => {
  //     API.networkObject.getAllWithinPolygon(
  //       "buildings",
  //       null,
  //       null,
  //       null,
  //       "coordinates",
  //       false,
  //       polygon,
  //       projection,
  //       true,
  //       result => {
  //         resolve(result.data.data);
  //       },
  //       error => {
  //         reject(error);
  //       }
  //     );
  //   });
  // },

  GET_ALL_OBJECTS_IN_GIVEN_WORKSPACE({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.getAllInGivenWorkspace(
        data.endpoint,
        data.workspaceUUID,
        data.technicalType,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  CLEAR_MULTI_OBJECT_SELECTION_DATA({ commit }) {
    commit("clearMultiObjectSelection");
  },

  fetchObjectByUUID(
    { commit },
    {
      endpoint,
      objectUUID,
      fieldsToInclude = null,
      fieldsToAppend = null,
      workspaceUuidInclude = true
    }
  ) {
    return new Promise((resolve, reject) => {
      API.networkObject.getByUUID(
        endpoint,
        objectUUID,
        fieldsToInclude,
        fieldsToAppend,
        workspaceUuidInclude,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  CREATE_OBJECTS({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.createInBulk(
        data.endpoint,
        data.data,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  CLONE_OBJECTS({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.cloneInBulk(
        data.endpoint,
        data.data,
        result => {
          resolve(result.data.data[0]);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  EDIT_OBJECTS({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.editInBulk(
        data.endpoint,
        data.data,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  deleteObjectByUUID({ commit }, { endpoint, objectUUID, action = null }) {
    return new Promise((resolve, reject) => {
      API.networkObject.delete(
        endpoint,
        objectUUID,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        },
        action
      );
    });
  },

  DELETE_OBJECTS({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.deleteInBulk(
        data.endpoint,
        data.objects,
        resp => {
          resolve(resp);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  DELETE_PHOTO({ commit }, photo) {
    return new Promise((resolve, reject) => {
      API.networkObject.delete(
        "images",
        photo.uuid,
        resp => {
          resolve(resp);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  UPDATE_SELECTED_OBJECT_IDENTIFIERS({ commit }, data) {
    commit("updateSelectedObjectIdentifiers", data);
  },

  UPDATE_SELECTED_OBJECT_DATA({ commit }, data) {
    commit("updateSelectedObjectData", data);
  },

  FETCH_ALL_OBJECTS_PROPERTIES_WITHOUT_FIELDS({ commit }, filters = null) {
    return new Promise((resolve, reject) => {
      API.object_form.getAllWithoutFields(
        filters,
        resp => {
          let objectProperties = resp.data.data;
          commit("setAllObjectsPropertiesWithoutFields", objectProperties);
          resolve(objectProperties);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  SEARCH_OBJECTS_BY_WORD({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.searchObjects(
        data.searchFor,
        data.word,
        data.layers,
        data.signalAbort,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  SEARCH_OBJECTS_BY_ID({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.searchObjectsById(
        data.searchFor,
        data.word,
        data.layers,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  SEARCH_PLACE_BY_WORD({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.searchAddress(
        data,
        data.cancelToken,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  SEARCH_OBJECTS_BY_ATTRIBUTE({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.searchByAttributes(
        data.layer,
        data.filter,
        data.value,
        data.params,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  SEARCH_NEW_PAGE({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.searchNewPaginationPage(
        data,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },


  GET_LAYER_ATTRIBUTES({ commit }, { layer, filter = null }) {
    return new Promise((resolve, reject) => {
      API.networkObject.getLayerAttributes(
        layer,
        filter,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  GET_AUTOMATIC_LABEL({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.object_form.getAutomaticLabel(
        data,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  LABEL_NETWORK({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.workspace_design.labelNetwork(
        data,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  setActiveNetworkFilters({ commit }, data) {
    commit("SET_ACTIVE_NETWORK_FILTERS", data);
  },

  fetchSlackNearestObjects({ commit }, newSlackData) {
    return new Promise((resolve, reject) => {
      API.networkObject.getSlackNearestObjects(
        newSlackData,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  setClosureWithoutTechPoint({ commit }, data) {
    commit("SET_CLOSURE_WITHOUT_TECH_POINT", data);
  },

  fetchFormFields({ commit }, filters) {
    return new Promise((resolve, reject) => {
      API.object_form.getFormFields(
        filters,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchConnectionsToJoinCables({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getConnectionsToJoinCables(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  joinCables({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.join(
        data,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchCablesInClosure({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getCablesInClosure(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchCablesOutClosure({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getCablesOutClosure(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  reverseCableDirection({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.reverseDirection(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchCablesPassingInTechPoint({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getCablesPassingInTechPoint(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchPathwaysPassingTechPoint({ commit }, uuid) {
    return new Promise((resolve, reject) => {
      API.networkObject.getPathwaysPassingTechPoint(
        uuid,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchObjectInWorkspaceOrderByAttribute({ commit }, { layer, attribute }) {
    return new Promise((resolve, reject) => {
      API.networkObject.getObjectInWorkspaceOrderByAttribute(
        layer,
        attribute,
        result => {
          resolve(result.data.data[0]);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchObstructionTypeFields({ commit }, { obstructionType, includes = null }) {
    return new Promise((resolve, reject) => {
      API.networkObject.getObstructionTypeFields(
        obstructionType,
        includes,
        result => {
          resolve(result.data.data);
        },
        error => {
          reject(error);
        }
      )
    })
  }
 };

function getLayerIndex(objectsProperties, layer) {
  return objectsProperties.findIndex(object => object.layer === layer);
}

function getMultiObjectSelectionTabs(layers) {
  let tabs = [];
  layers.forEach(layer => {
    if (
      store.getters.layerExistsInObjectForms(layer) &&
      store.getters.layerHasForm(layer)
    ) {
      let permissionName = store.getters.getLayerPermissionName(layer);
      let layerID = store.getters.getLayerOrderPosition(layer);
      if (
        permissionName !== undefined &&
        store.getters.userHasPermission(
          permissionName + "_list"
        )
      ) {
        tabs.push({ id: layerID, layer: layer });
      }
    }
  });
  tabs.sort((a, b) => (a.id > b.id ? 1 : -1));
  return tabs;
}

export default { state, getters, actions, mutations };



// WEBPACK FOOTER //
// ./src/store/modules/objects.js